import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useBrand } from '../context/BrandContext';
import { useAccount } from '../context/AccountContext';
import client from '../api/client';

// ── Skeleton component ────────────────────────────────────────
export const Skeleton = ({ width='100%', height=14, radius=6, circle=false, size, style={} }) => {
  const w = circle ? size : width;
  const h = circle ? size : height;
  return (
    <div style={{
      width:w, height:h,
      borderRadius: circle ? '50%' : radius,
      background:'linear-gradient(90deg,#f0f0f0 25%,#e0e0e0 50%,#f0f0f0 75%)',
      backgroundSize:'200% 100%',
      animation:'skeleton-shimmer 1.4s infinite',
      flexShrink:0, ...style,
    }} />
  );
};
export const SkeletonBlock = ({ rows=3, gap=10 }) => (
  <div style={{ display:'flex', flexDirection:'column', gap }}>
    {Array(rows).fill(null).map((_,i) => (
      <Skeleton key={i} width={i===rows-1?'60%':'100%'} height={14} />
    ))}
  </div>
);

// ── Nav definitions per sidebar mode ─────────────────────────
const PLATFORM_NAV = [
  { to:'/',                  icon:'ti-layout-dashboard', label:'Dashboard',   exact:true },
  { to:'/platform/accounts', icon:'ti-building',         label:'Accounts'              },
  { to:'/platform/plans',    icon:'ti-credit-card',      label:'Plans'                 },
  { to:'/integrations',      icon:'ti-plug-connected',   label:'Integrations'          },
  { to:'/settings',          icon:'ti-settings',         label:'Settings'              },
];
const AGENCY_NAV_BASE = [
  { to:'/',                   icon:'ti-layout-dashboard', label:'Dashboard', exact:true },
  { to:'/agency/accounts',    icon:'ti-building-store',   label:'Sub-Accounts'         },
  { to:'/integrations',       icon:'ti-plug-connected',   label:'Integrations'         },
  { to:'/billing',            icon:'ti-credit-card',      label:'Billing'              },
  { to:'/settings',           icon:'ti-settings',         label:'Settings'             },
];
const AGENCY_WHITELABEL_EXTRA = [
  { to:'/agency/branding',    icon:'ti-palette',          label:'Branding'             },
  { to:'/agency/domain',      icon:'ti-world',            label:'Custom Domain'        },
];
const SUB_NAV = [
  { to:'/',         icon:'ti-layout-dashboard', label:'Dashboard',  exact:true         },
  { to:'/leads',    icon:'ti-users',            label:'Contacts'                       },
  { to:'/pipeline', icon:'ti-git-branch',       label:'Pipeline'                       },
  { to:'/email',    icon:'ti-mail',             label:'Email'                          },
  { to:'/calendar', icon:'ti-calendar',         label:'Calendar'                       },
  { to:'/team',     icon:'ti-user-plus',        label:'Team',       minRole:'account_admin' },
  { to:'/integrations', icon:'ti-plug-connected', label:'Integrations'                 },
  { to:'/billing',  icon:'ti-credit-card',      label:'Billing'                        },
  { to:'/settings', icon:'ti-settings',         label:'Settings'                       },
];
const ROLE_RANK = { account_user:0, account_admin:1, agency_user:1, agency_admin:2, platform_admin:3, owner:4 };
const hasMinRole = (role, min) => (ROLE_RANK[role]??0) >= (ROLE_RANK[min]??0);
const CAN_SWITCH = ['owner','platform_admin','agency_admin','agency_user'];

// ── Account switcher slide-in panel ──────────────────────────
const SwitcherPanel = ({ open, onClose, onSwitch, activeAccountId, userRole }) => {
  const [accounts, setAccounts] = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [search,   setSearch]   = useState('');
  const inputRef = useRef(null);
  useEffect(() => {
    if (!open) { setSearch(''); return; }
    setLoading(true);
    client.get('/api/accounts')
      .then(({ data }) => setAccounts(data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [open]);
  const isPlatformLevel = ['owner','platform_admin'].includes(userRole);
  const filtered = accounts.filter(a => !search || a.name.toLowerCase().includes(search.toLowerCase()));
  const groups = isPlatformLevel
    ? [
        { label:'PLATFORM',        type:'platform', items: filtered.filter(a=>a.type==='platform') },
        { label:'AGENCY ACCOUNTS', type:'agency',   items: filtered.filter(a=>a.type==='agency')   },
        { label:'SUB-ACCOUNTS',    type:'sub',       items: filtered.filter(a=>a.type==='sub')      },
      ].filter(g=>g.items.length>0)
    : [{ label:'SUB-ACCOUNTS', type:'sub', items: filtered }];
  const TYPE_COLOR = { platform:'#8b5cf6', agency:'#0099FF', sub:'#22c55e' };
  return (
    <>
      {open && <div onClick={onClose} style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.3)',zIndex:998}}/>}
      <div style={{
        position:'fixed',top:0,left:0,height:'100vh',width:300,
        background:'#fff',zIndex:999,boxShadow:'4px 0 24px rgba(0,0,0,0.15)',
        transform:open?'translateX(0)':'translateX(-100%)',
        transition:'transform 0.25s ease',
        display:'flex',flexDirection:'column',
      }}>
        <div style={{padding:'16px 16px 12px',borderBottom:'1px solid #f3f4f6'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:10}}>
            <div style={{fontWeight:700,fontSize:14,color:'#151515'}}>Switch Account</div>
            <button onClick={onClose} style={{background:'none',border:'none',cursor:'pointer',color:'#9ca3af',fontSize:18,padding:2}}><i className="ti ti-x"/></button>
          </div>
          <div style={{position:'relative'}}>
            <i className="ti ti-search" style={{position:'absolute',left:10,top:'50%',transform:'translateY(-50%)',color:'#9ca3af',fontSize:14}}/>
            <input ref={inputRef} placeholder="Search accounts..." value={search} onChange={e=>setSearch(e.target.value)}
              style={{width:'100%',padding:'8px 10px 8px 32px',borderRadius:8,border:'1px solid #e5e7eb',fontSize:13,outline:'none',boxSizing:'border-box'}}/>
          </div>
        </div>
        <div style={{flex:1,overflowY:'auto'}}>
          {loading?(
            <div style={{padding:16,display:'flex',flexDirection:'column',gap:10}}>
              {Array(5).fill(null).map((_,i)=>(
                <div key={i} style={{display:'flex',alignItems:'center',gap:10}}>
                  <Skeleton circle size={36}/><div style={{flex:1}}><Skeleton height={12} width="60%" style={{marginBottom:5}}/><Skeleton height={10} width="40%"/></div>
                </div>
              ))}
            </div>
          ):groups.map(group=>(
            <div key={group.type}>
              <div style={{padding:'10px 16px 4px',fontSize:10,fontWeight:700,color:'#9ca3af',letterSpacing:0.8}}>{group.label}</div>
              {group.items.map(acc=>(
                <button key={acc.id} onClick={()=>onSwitch(acc.id)}
                  style={{width:'100%',padding:'10px 16px',border:'none',textAlign:'left',cursor:'pointer',
                    background:acc.id===activeAccountId?'#f0f9ff':'#fff',
                    borderLeft:acc.id===activeAccountId?'3px solid #0099FF':'3px solid transparent',
                    display:'flex',alignItems:'center',gap:10,transition:'background 0.1s'}}
                  onMouseEnter={e=>{if(acc.id!==activeAccountId)e.currentTarget.style.background='#f9fafb';}}
                  onMouseLeave={e=>{if(acc.id!==activeAccountId)e.currentTarget.style.background='#fff';}}>
                  <div style={{width:36,height:36,borderRadius:8,background:TYPE_COLOR[acc.type]||'#0099FF',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,color:'#fff',fontSize:14,fontWeight:700}}>
                    {acc.name?.charAt(0).toUpperCase()}
                  </div>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:13,fontWeight:acc.id===activeAccountId?600:400,color:'#151515',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{acc.name}</div>
                    <div style={{fontSize:10,color:'#9ca3af',marginTop:1}}>{acc.plan?.name||acc.type} · {acc.status}</div>
                  </div>
                  {acc.id===activeAccountId&&<i className="ti ti-check" style={{fontSize:14,color:'#0099FF',flexShrink:0}}/>}
                </button>
              ))}
            </div>
          ))}
          {!loading&&filtered.length===0&&<div style={{padding:24,textAlign:'center',color:'#9ca3af',fontSize:13}}>No accounts found</div>}
        </div>
      </div>
    </>
  );
};

// ── Main Layout ───────────────────────────────────────────────
export default function Layout() {
  const { user, logout, activeAccountId, switchAccount, isSwitched, exitSwitchedAccount } = useAuth();
  const { activeAccount, isPlatform, isAgency, isWhiteLabel, loading: accountLoading } = useAccount();
  const { brand } = useBrand();
  const navigate = useNavigate();

  const [loggingOut,   setLoggingOut]   = useState(false);
  const [switcherOpen, setSwitcherOpen] = useState(false);

  // ── Trial / billing status ────────────────────────────────
  // Only fetch for sub-account users — never for owner/platform/agency roles
  const [billingStatus, setBillingStatus] = useState(null);
  // Platform owners/admins never see trial banners
  // Agency admins see billing (they pay the platform)
  // Sub-account admins see billing (they pay the agency or platform)
  const SKIP_BILLING_ROLES = ['owner', 'platform_admin'];
  useEffect(() => {
    if (!user) return;
    if (SKIP_BILLING_ROLES.includes(user?.role)) return;
    if (isSwitched) return; // admin viewing another account — don't show their billing
    if (accountLoading) return;
    client.get('/api/billing/status')
      .then(({ data }) => setBillingStatus(data))
      .catch(() => {});
  }, [user, isSwitched, accountLoading]); // eslint-disable-line

  const canSwitch = CAN_SWITCH.includes(user?.role);

  useEffect(() => {
    const handler = (e) => { if (e.key==='Escape') setSwitcherOpen(false); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, []);

  const handleLogout = async () => {
    setLoggingOut(true);
    await logout();
    navigate('/login');
  };
  const handleSwitch = useCallback((accountId) => {
    switchAccount(accountId);
    setSwitcherOpen(false);
    window.location.href = '/';
  }, [switchAccount]);
  const handleExitSwitch = () => { exitSwitchedAccount(); window.location.href = '/'; };
  const initials = (name='') => name.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase();

  const navItems = isPlatform
    ? PLATFORM_NAV
    : isAgency
      ? [...AGENCY_NAV_BASE, ...(isWhiteLabel ? AGENCY_WHITELABEL_EXTRA : [])]
      : SUB_NAV.filter(n => !n.minRole || hasMinRole(user?.role, n.minRole));

  const sidebarBrand = (isAgency && isWhiteLabel) ? brand : {
    primary_color: brand.primary_color || '#0099FF',
    dark_color:    brand.dark_color    || '#151515',
    logo_url:      brand.logo_url,
    app_name:      isPlatform||(isAgency&&!isWhiteLabel) ? (brand.app_name||'Digital Xtension') : brand.app_name,
    tagline:       brand.tagline,
    hide_name:     brand.hide_name,
  };
  const primary = sidebarBrand.primary_color || '#0099FF';
  const dark    = sidebarBrand.dark_color    || '#151515';

  const switcherLabel = accountLoading ? '...'
    : isSwitched ? (activeAccount?.name||'Switched Account')
    : isPlatform  ? 'All Accounts'
    : isAgency    ? 'Sub-Accounts'
    : null;

  const renderLogo = () => {
    const { logo_url, app_name, tagline, hide_name } = sidebarBrand;
    if (logo_url && hide_name) {
      return <img src={logo_url} alt={app_name||'Logo'} style={{width:'100%',height:40,objectFit:'contain',objectPosition:'left center',display:'block',padding:'0 10px',boxSizing:'border-box'}}/>;
    }
    if (logo_url && !hide_name) {
      return (
        <div style={{display:'flex',alignItems:'center',gap:10}}>
          <img src={logo_url} alt={app_name||'Logo'} style={{width:32,height:32,objectFit:'contain',borderRadius:4,flexShrink:0}}/>
          <div style={{minWidth:0}}>
            <div style={{color:'#fff',fontWeight:700,fontSize:14,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{app_name}</div>
            {tagline&&<div style={{color:primary,fontSize:9,letterSpacing:1.5,marginTop:2}}>{tagline}</div>}
          </div>
        </div>
      );
    }
    return (
      <div style={{display:'flex',alignItems:'center',gap:8}}>
        <i className="ti ti-bolt" style={{fontSize:20,color:primary,flexShrink:0}}/>
        <div style={{minWidth:0}}>
          {app_name&&<div style={{color:'#fff',fontWeight:700,fontSize:14,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{app_name}</div>}
          {tagline&&<div style={{color:primary,fontSize:9,letterSpacing:1.5,marginTop:2}}>{tagline}</div>}
        </div>
      </div>
    );
  };

  // ── Trial banner helpers ──────────────────────────────────
  const daysLeft = billingStatus?.days_left ?? 0;
  const bStatus  = billingStatus?.status;
  const showYellowBanner = bStatus==='trial' && daysLeft<=7 && daysLeft>0;
  const showRedBanner    = bStatus==='trial' && daysLeft===0;
  const showExpiredBanner= bStatus==='trial_expired';

  return (
    <div style={{display:'flex',height:'100vh',fontFamily:"'Inter',system-ui,sans-serif",overflow:'hidden'}}>
      <style>{`
        @keyframes skeleton-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}
        @keyframes spin{to{transform:rotate(360deg)}}
        .nav-link-hover:hover{background:rgba(255,255,255,0.06) !important;}
      `}</style>

      <SwitcherPanel open={switcherOpen} onClose={()=>setSwitcherOpen(false)} onSwitch={handleSwitch} activeAccountId={activeAccountId} userRole={user?.role}/>

      {/* ── Sidebar ── */}
      <aside style={{width:220,background:dark,display:'flex',flexDirection:'column',flexShrink:0,zIndex:10}}>
        <div style={{padding:sidebarBrand.logo_url&&sidebarBrand.hide_name?'14px 0':'16px',borderBottom:'1px solid rgba(255,255,255,0.08)',minHeight:64,display:'flex',alignItems:'center'}}>
          {renderLogo()}
        </div>
        {canSwitch&&(
          <button onClick={()=>setSwitcherOpen(true)}
            style={{margin:'10px 10px 2px',padding:'9px 12px',borderRadius:10,border:'1px solid rgba(255,255,255,0.12)',background:'rgba(255,255,255,0.06)',cursor:'pointer',display:'flex',alignItems:'center',gap:8,textAlign:'left',transition:'background 0.15s'}}
            onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.1)'}
            onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.06)'}>
            <div style={{width:28,height:28,borderRadius:6,background:`${primary}33`,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
              <i className={`ti ${isPlatform?'ti-bolt':isAgency?'ti-building-store':'ti-building'}`} style={{fontSize:14,color:primary}}/>
            </div>
            <div style={{flex:1,minWidth:0}}>
              <div style={{fontSize:12,fontWeight:600,color:'#fff',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>
                {activeAccount?.name||sidebarBrand.app_name||'Digital Xtension'}
              </div>
              <div style={{fontSize:10,color:'rgba(255,255,255,0.45)',marginTop:1}}>{switcherLabel}</div>
            </div>
            <i className="ti ti-selector" style={{fontSize:14,color:'rgba(255,255,255,0.4)',flexShrink:0}}/>
          </button>
        )}
        {canSwitch&&<div style={{margin:'8px 10px 4px',borderTop:'1px solid rgba(255,255,255,0.08)'}}/>}
        {isSwitched&&(
          <div style={{margin:'0 10px 4px',background:'#f59e0b18',border:'1px solid #f59e0b33',borderRadius:8,padding:'6px 10px',display:'flex',alignItems:'center',gap:6}}>
            <i className="ti ti-eye" style={{fontSize:12,color:'#f59e0b',flexShrink:0}}/>
            <span style={{fontSize:10,color:'#f59e0b',flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>Viewing sub-account</span>
            <button onClick={handleExitSwitch} style={{background:'none',border:'none',cursor:'pointer',color:'#f59e0b',fontSize:10,padding:0,fontWeight:600,flexShrink:0}}>Exit</button>
          </div>
        )}
        <nav style={{flex:1,padding:'4px 0',overflowY:'auto'}}>
          {navItems.map(n=>(
            <NavLink key={n.to} to={n.to} end={n.exact} className="nav-link-hover"
              style={({isActive})=>({
                display:'flex',alignItems:'center',gap:10,
                padding:'9px 16px',textDecoration:'none',margin:'1px 8px',borderRadius:8,
                background:isActive?`${primary}22`:'transparent',
                borderLeft:isActive?`3px solid ${primary}`:'3px solid transparent',
                color:isActive?primary:'rgba(255,255,255,0.65)',
                fontSize:13,fontWeight:isActive?600:400,transition:'all 0.1s',
              })}>
              <i className={`ti ${n.icon}`} style={{fontSize:16}}/>{n.label}
            </NavLink>
          ))}
        </nav>
        <div style={{padding:'10px 12px',borderTop:'1px solid rgba(255,255,255,0.08)',display:'flex',alignItems:'center',gap:10}}>
          <div style={{width:32,height:32,borderRadius:'50%',background:primary,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:11,fontWeight:700,flexShrink:0}}>
            {initials(user?.name)}
          </div>
          <div style={{flex:1,minWidth:0}}>
            <div style={{color:'#fff',fontSize:12,fontWeight:500,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{user?.name}</div>
            <div style={{color:'rgba(255,255,255,0.4)',fontSize:10}}>{user?.role?.replace(/_/g,' ')}</div>
          </div>
          <button onClick={handleLogout} disabled={loggingOut} title="Logout"
            style={{background:'none',border:'none',cursor:'pointer',color:'rgba(255,255,255,0.4)',padding:0,fontSize:16}}>
            <i className={`ti ${loggingOut?'ti-loader':'ti-logout'}`} style={loggingOut?{animation:'spin 0.8s linear infinite',display:'inline-block'}:{}}/>
          </button>
        </div>
      </aside>

      {/* ── Main content ── */}
      <div style={{flex:1,display:'flex',flexDirection:'column',overflow:'hidden'}}>
        {/* Header */}
        <header style={{background:'#fff',borderBottom:'1px solid #e5e7eb',padding:'0 24px',height:52,display:'flex',alignItems:'center',justifyContent:'space-between',flexShrink:0}}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            {isSwitched&&(
              <div style={{display:'flex',alignItems:'center',gap:6,background:'#fef3c7',border:'1px solid #fde68a',borderRadius:6,padding:'4px 10px',fontSize:11,color:'#92400e',fontWeight:500}}>
                <i className="ti ti-eye" style={{fontSize:12}}/>
                Viewing: {activeAccount?.name||'...'}
                <button onClick={handleExitSwitch} style={{background:'none',border:'none',cursor:'pointer',color:'#92400e',fontSize:11,fontWeight:700,padding:'0 0 0 6px'}}>✕</button>
              </div>
            )}
            {!isSwitched&&(
              <span style={{fontSize:12,color:'#9ca3af'}}>
                {isPlatform?'Platform Dashboard':isAgency?'Agency Dashboard':(sidebarBrand.app_name||'Digital Xtension')}
              </span>
            )}
          </div>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <span style={{fontSize:12,color:'#6b7280'}}>{user?.email}</span>
            <div style={{width:32,height:32,borderRadius:'50%',background:primary,display:'flex',alignItems:'center',justifyContent:'center',color:'#fff',fontSize:11,fontWeight:700}}>
              {initials(user?.name)}
            </div>
          </div>
        </header>

        {/* ── Trial warning banners — shown between header and content ── */}
        {(showYellowBanner||showRedBanner)&&(
          <div style={{
            background:daysLeft<=2?'#fef2f2':'#fffbeb',
            borderBottom:`1px solid ${daysLeft<=2?'#fecaca':'#fde68a'}`,
            padding:'9px 24px',display:'flex',alignItems:'center',gap:10,fontSize:13,flexShrink:0,
          }}>
            <i className={`ti ${daysLeft<=2?'ti-alert-circle':'ti-clock'}`}
              style={{color:daysLeft<=2?'#ef4444':'#f59e0b',fontSize:16,flexShrink:0}}/>
            <span style={{color:daysLeft<=2?'#991b1b':'#92400e',flex:1}}>
              {daysLeft===0
                ? '⚠️ Your trial ends today. Upgrade now to keep access.'
                : `Your trial ends in ${daysLeft} day${daysLeft!==1?'s':''}. Upgrade to keep all features.`}
            </span>
            <NavLink to="/billing"
              style={{padding:'5px 14px',borderRadius:6,background:daysLeft<=2?'#ef4444':'#f59e0b',color:'#fff',fontWeight:600,fontSize:12,textDecoration:'none',flexShrink:0}}>
              Upgrade Now
            </NavLink>
          </div>
        )}
        {showExpiredBanner&&(
          <div style={{background:'#fef2f2',borderBottom:'1px solid #fecaca',padding:'10px 24px',display:'flex',alignItems:'center',gap:10,fontSize:13,flexShrink:0}}>
            <i className="ti ti-lock" style={{color:'#ef4444',fontSize:18,flexShrink:0}}/>
            <span style={{color:'#991b1b',fontWeight:500,flex:1}}>
              Your trial has expired. Please upgrade to continue using Digital Xtension.
            </span>
            <NavLink to="/billing"
              style={{padding:'6px 16px',borderRadius:6,background:'#ef4444',color:'#fff',fontWeight:700,fontSize:12,textDecoration:'none',flexShrink:0}}>
              Upgrade Now →
            </NavLink>
          </div>
        )}

        <main style={{flex:1,overflow:'auto',padding:24,background:'#f5f7fa'}}>
          <Outlet/>
        </main>
      </div>
    </div>
  );
}
